import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { Customer } from '../lib/customers';
import { CustomerSourcesService, CustomerSource } from '../lib/customerSources';
import SelectCustomerModal from './manage/SelectCustomerModal';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (projectId?: string) => void;
  initialCustomer?: Customer | null;
  initialOpportunityId?: number | null;
  navigateToProject?: boolean;
}

export default function CreateProjectModal({
  isOpen,
  onClose,
  onSuccess,
  initialCustomer,
  initialOpportunityId,
  navigateToProject = false
}: CreateProjectModalProps) {
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(initialCustomer || null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [customerSources, setCustomerSources] = useState<CustomerSource[]>([]);
  const [selectedCustomerSource, setSelectedCustomerSource] = useState<CustomerSource | null>(null);
  const [customerSourcesLoading, setCustomerSourcesLoading] = useState(false);
  const customerSourceButtonRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setTitle('New project');
      setSelectedCustomer(initialCustomer || null);
      setSelectedCustomerSource(null);
    }
  }, [isOpen, initialCustomer]);

  useEffect(() => {
    const loadCustomerSources = async () => {
      if (!selectedBusiness?.id || !isOpen) return;
      try {
        setCustomerSourcesLoading(true);
        const sources = await CustomerSourcesService.getCustomerSources(selectedBusiness.id);
        const defaultSources: CustomerSource[] = [
          {
            id: 'repeat-customer',
            business_id: selectedBusiness.id,
            name: 'Repeat Customer',
            is_active: true,
            created_at: '',
            updated_at: ''
          },
          {
            id: 'referral',
            business_id: selectedBusiness.id,
            name: 'Referral',
            is_active: true,
            created_at: '',
            updated_at: ''
          }
        ];

        const mergedSources = [...defaultSources, ...sources].filter(
          (source, index, self) =>
            index === self.findIndex(s => s.name.toLowerCase() === source.name.toLowerCase())
        );

        setCustomerSources(mergedSources.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error('Error loading customer sources:', error);
      } finally {
        setCustomerSourcesLoading(false);
      }
    };

    loadCustomerSources();
  }, [isOpen, selectedBusiness?.id]);

  const detectCustomerSource = async (customer: Customer) => {
    if (!selectedBusiness?.id) return;

    try {
      if (customer.repeat === true) {
        setSelectedCustomerSource({
          id: 'repeat-customer',
          business_id: selectedBusiness.id,
          name: 'Repeat Customer',
          is_active: true,
          created_at: '',
          updated_at: ''
        });
        return;
      }

      if (!supabase) return;

      const { data: bookingsData, error } = await supabase
        .from('bookings')
        .select('id')
        .eq('customer_id', customer.id)
        .eq('business_id', selectedBusiness.id);

      if (error) {
        console.error('Error counting customer bookings:', error);
        return;
      }

      const totalBookings = bookingsData?.length || 0;
      if (totalBookings >= 1) {
        setSelectedCustomerSource({
          id: 'repeat-customer',
          business_id: selectedBusiness.id,
          name: 'Repeat Customer',
          is_active: true,
          created_at: '',
          updated_at: ''
        });

        try {
          const { error: updateError } = await supabase
            .from('customers')
            .update({
              repeat: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', customer.id);

          if (updateError) {
            console.error('Error updating customer repeat status:', updateError);
          }
        } catch (updateError) {
          console.error('Error updating customer repeat status:', updateError);
        }
      } else {
        setSelectedCustomerSource(null);
      }
    } catch (error) {
      console.error('Error detecting customer source:', error);
    }
  };

  useEffect(() => {
    if (!isOpen || !selectedCustomer) return;
    if (selectedCustomerSource) return;
    detectCustomerSource(selectedCustomer);
  }, [isOpen, selectedCustomer?.id]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Please enter a project title');
      return;
    }

    if (!selectedBusiness?.id) {
      toast.error('No business selected');
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('projects')
        .insert({
          business_id: selectedBusiness.id,
          title: title.trim(),
          status: 'draft',
          customer_id: selectedCustomer?.id || null,
          customer_source: selectedCustomerSource?.name || null,
          pipeline_opportunities_id: initialOpportunityId || null
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating project:', error);
        toast.error('Failed to create project');
        return;
      }

      toast.success('Project created successfully');
      handleClose();
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(data.id);
      }
      
      // Navigate to projects page and open the project if requested
      if (navigateToProject && data.id) {
        // Navigate to projects page with the project ID in state
        navigate('/projects', {
          state: {
            openProjectId: data.id
          }
        });
      }
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 99999,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 99999
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6">
            <h2 className="text-2xl font-bold text-gray-900">Create Project</h2>
            <p className="mt-1 text-sm text-gray-500">
              Add a new project to track and manage
            </p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Project Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Title <span className="text-red-500">*</span>
                </label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter project title..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  disabled={loading}
                  autoFocus
                />
              </div>

              {/* Customer Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer
                </label>
                {selectedCustomer ? (
                  <div className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                        {selectedCustomer.first_name?.[0] || ''}{selectedCustomer.last_name?.[0] || ''}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </div>
                        <div className="flex flex-col gap-0.5 mt-0.5">
                          {selectedCustomer.email && (
                            <div className="text-xs text-gray-500 truncate">{selectedCustomer.email}</div>
                          )}
                          {selectedCustomer.phone && (
                            <div className="text-xs text-gray-500 truncate">{selectedCustomer.phone}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsSelectCustomerModalOpen(true)}
                      className="ml-3 px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                      disabled={loading}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsSelectCustomerModalOpen(true)}
                    className="w-full px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center space-x-2"
                    disabled={loading}
                  >
                    <Users className="h-4 w-4" />
                    <span>Select Customer</span>
                  </button>
                )}
              </div>

              {/* Customer Source */}
              {selectedCustomer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Source
                  </label>
                  <div className="relative">
                    <select
                      ref={customerSourceButtonRef}
                      value={selectedCustomerSource?.name || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!value) {
                          setSelectedCustomerSource(null);
                          return;
                        }
                        const matchingSource = customerSources.find(
                          source => source.name.toLowerCase() === value.toLowerCase()
                        );
                        if (matchingSource) {
                          setSelectedCustomerSource(matchingSource);
                        } else if (selectedBusiness?.id) {
                          setSelectedCustomerSource({
                            id: 'project-source',
                            business_id: selectedBusiness.id,
                            name: value,
                            is_active: true,
                            created_at: '',
                            updated_at: ''
                          });
                        }
                      }}
                      disabled={loading || customerSourcesLoading}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all ${
                        selectedCustomerSource ? 'border-gray-300 bg-white' : 'border-red-300 bg-red-50'
                      }`}
                    >
                      <option value="">
                        {customerSourcesLoading ? 'Loading sources...' : 'Select source...'}
                      </option>
                      {customerSources.map(source => (
                        <option key={source.id} value={source.name}>
                          {source.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !title.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Select Customer Modal */}
      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={(customer) => {
          setSelectedCustomer(customer);
          setSelectedCustomerSource(null);
          setIsSelectCustomerModalOpen(false);
        }}
        businessId={selectedBusiness?.id}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}
